/**
 * PlayerBar 하위 컴포넌트가 공유하는 재생 상태 집계 훅.
 * QueturePlayer 의 Handle 과 콜백을 연결한다.
 */
import { useCallback, useState, type RefObject } from 'react'
import type { QueturePlayerHandle } from '@quebon/shared-learning'

export interface PlayerState {
  isPlaying: boolean
  currentSec: number
  durationSec: number
  playbackRate: number
  isMuted: boolean
  volume: number
  maxProgressSec: number
  onPlayStateChange: (playing: boolean) => void
  onPlaybackRateChange: (rate: number) => void
  onProgress: (progress: number, currentTime: number, duration: number) => void
  togglePlay: () => void
  seek: (seconds: number) => void
  setRate: (rate: number) => void
  toggleMute: () => void
  setVolumeValue: (v: number) => void
}

export function usePlayerState(playerRef: RefObject<QueturePlayerHandle | null>): PlayerState {
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentSec, setCurrentSec] = useState(0)
  const [durationSec, setDurationSec] = useState(0)
  const [playbackRate, setPlaybackRate] = useState(1)
  const [isMuted, setIsMuted] = useState(false)
  const [volume, setVolume] = useState(1)
  const [maxProgressSec, setMaxProgressSec] = useState(0)

  const onPlayStateChange = useCallback((playing: boolean) => setIsPlaying(playing), [])
  const onPlaybackRateChange = useCallback((rate: number) => setPlaybackRate(rate), [])
  const onProgress = useCallback((_p: number, t: number, d: number) => {
    setCurrentSec(t)
    setDurationSec(d)
    setMaxProgressSec((prev) => Math.max(prev, t))
  }, [])

  const togglePlay = useCallback(() => {
    const ref = playerRef.current
    if (!ref) return
    if (ref.isPaused()) ref.play()
    else ref.pause()
  }, [playerRef])

  const seek = useCallback((seconds: number) => {
    const ref = playerRef.current
    if (!ref) return
    setCurrentSec(seconds)
    ref.seek(seconds)
  }, [playerRef])

  const setRate = useCallback((rate: number) => {
    playerRef.current?.setPlaybackRate(rate)
  }, [playerRef])

  const toggleMute = useCallback(() => {
    const next = !isMuted
    playerRef.current?.setMuted(next)
    setIsMuted(next)
  }, [playerRef, isMuted])

  const setVolumeValue = useCallback((v: number) => {
    const clamped = Math.max(0, Math.min(1, v))
    playerRef.current?.setVolume(clamped)
    setVolume(clamped)
    if (clamped > 0 && isMuted) {
      playerRef.current?.setMuted(false)
      setIsMuted(false)
    }
  }, [playerRef, isMuted])

  return {
    isPlaying, currentSec, durationSec, playbackRate, isMuted, volume, maxProgressSec,
    onPlayStateChange, onPlaybackRateChange, onProgress,
    togglePlay, seek, setRate, toggleMute, setVolumeValue,
  }
}
